import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { fichaService } from '../../services/fichaService'
import MobileCard, { MobileCardContent } from '../../components/ui/MobileCard'
import Button from '../../components/ui/Button'
import { ArrowLeft, FileText, Calendar, Heart, Building2, User, Stethoscope, Download, AlertCircle, Clock, Activity, Pill } from 'lucide-react'
import LoadingState from '../../components/ui/LoadingState'
import EmptyState from '../../components/ui/EmptyState'
import { normalizeFotoUrl } from '../../utils/avatarHelpers'

function extractArray(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.data?.data)) return payload.data.data
  return []
}

export default function FichaDetailMobile() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [ficha, setFicha] = useState(null)
  const [tratamientos, setTratamientos] = useState([])
  const [seguimientos, setSeguimientos] = useState([])
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    if (id) {
      fetchFichaDetail()
    }
  }, [id])

  const fetchFichaDetail = async () => {
    try {
      setLoading(true)
      const [fichaResponse, tratamientosResponse, seguimientosResponse] = await Promise.all([
        fichaService.getById(id),
        fichaService.getTratamientos(id),
        fichaService.getSeguimientos(id)
      ])

      const fichaData = fichaResponse?.data?.data || fichaResponse?.data || fichaResponse
      setFicha(fichaData || null)
      setTratamientos(extractArray(tratamientosResponse))
      setSeguimientos(extractArray(seguimientosResponse))
    } catch (error) {
      console.error('Error fetching ficha:', error)
      setFicha(null)
      setTratamientos([])
      setSeguimientos([])
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true)
      await fichaService.descargarPDF(id)
    } catch (error) {
      console.error('Error al descargar PDF:', error)
      alert(error.message || 'Error al descargar el PDF')
    } finally {
      setDownloading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Fecha no disponible'
    const date = new Date(dateString)
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Fecha no disponible'
    const date = new Date(dateString)
    return date.toLocaleString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getEstadoColor = (estado) => {
    if (!estado) return 'bg-gray-100 text-gray-700'
    switch (estado.toUpperCase()) {
      case 'CERRADO':
      case 'RESUELTO':
        return 'bg-green-100 text-green-700'
      case 'EN_TRATAMIENTO':
      case 'EN TRATAMIENTO':
        return 'bg-blue-100 text-blue-700'
      case 'URGENTE':
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-yellow-100 text-yellow-700'
    }
  }

  if (loading) {
    return <LoadingState message="Cargando ficha clínica..." />
  }

  if (!ficha) {
    return (
      <div className="px-4 py-6">
        <EmptyState
          icon={FileText}
          title="Ficha no encontrada"
          message="No se pudo cargar la información de la ficha clínica"
        />
      </div>
    )
  }

  const mascotaFoto = ficha.mascota?.foto || ficha.mascota?.avatar_url
  const getMascotaImage = () => {
    if (mascotaFoto) {
      return normalizeFotoUrl(mascotaFoto)
    }
    return '/placeholder-pet.svg'
  }

  return (
    <div className="min-h-full bg-transparent pb-24">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/95 border-b border-pastel-pink backdrop-blur-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-white hover:bg-gray-100 transition-colors shadow-sm"
            aria-label="Volver"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Ficha Clínica</h1>
          <div className="w-9" /> {/* Spacer para centrar */}
        </div>
      </div>

      {/* Mascota Image Section */}
      <div className="relative w-full aspect-square bg-gradient-to-br from-teal-50 via-teal-100 to-blue-100 overflow-hidden">
        <img
          src={getMascotaImage()}
          alt={ficha.mascota?.nombre || 'Mascota'}
          className="w-full h-full object-contain p-4"
          onError={(e) => {
            e.target.style.display = 'none'
            e.target.nextElementSibling.style.display = 'flex'
          }}
        />
        <div className="hidden w-full h-full items-center justify-center">
          <Heart className="w-24 h-24 text-pastel-teal" />
        </div>
      </div>

      {/* Ficha Info Card */}
      <div className="bg-white rounded-t-3xl -mt-6 relative z-10 px-4 pt-6 pb-6">
        {/* Nombre y estado */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              {ficha.mascota?.nombre || 'Mascota'}
            </h2>
            <p className="text-sm text-gray-600">
              {ficha.mascota?.especie || ''}
              {ficha.mascota?.raza && ` • ${ficha.mascota.raza}`}
            </p>
          </div>
          {ficha.estado_caso || ficha.estadoCaso ? (
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getEstadoColor(ficha.estado_caso || ficha.estadoCaso)}`}>
              {ficha.estado_caso || ficha.estadoCaso}
            </span>
          ) : null}
        </div>

        {/* Información General */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center space-x-3 text-sm">
            <Calendar className="w-5 h-5 text-pastel-teal flex-shrink-0" />
            <div>
              <p className="text-gray-500">Fecha</p>
              <p className="font-medium text-gray-900">
                {formatDate(ficha.fecha || ficha.fecha_creacion || ficha.fechaCreacion)}
              </p>
            </div>
          </div>

          {ficha.veterinario && (
            <div className="flex items-center space-x-3 text-sm">
              <Stethoscope className="w-5 h-5 text-pastel-teal flex-shrink-0" />
              <div>
                <p className="text-gray-500">Veterinario</p>
                <p className="font-medium text-gray-900">
                  Dr. {ficha.veterinario.nombre || ''} {ficha.veterinario.apellido || ''}
                </p>
              </div>
            </div>
          )}

          {ficha.clinica && (
            <div className="flex items-center space-x-3 text-sm">
              <Building2 className="w-5 h-5 text-pastel-teal flex-shrink-0" />
              <div>
                <p className="text-gray-500">Clínica</p>
                <p className="font-medium text-gray-900">
                  {ficha.clinica.nombre || ficha.clinica.nombreClinica}
                </p>
              </div>
            </div>
          )}

          {ficha.numero_ficha || ficha.numeroFicha ? (
            <div className="flex items-center space-x-3 text-sm">
              <FileText className="w-5 h-5 text-pastel-teal flex-shrink-0" />
              <div>
                <p className="text-gray-500">Número de Ficha</p>
                <p className="font-medium text-gray-900">
                  {ficha.numero_ficha || ficha.numeroFicha}
                </p>
              </div>
            </div>
          ) : null}
        </div>

        {/* Diagnóstico */}
        {ficha.diagnostico && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Diagnóstico</h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              {ficha.diagnostico}
            </p>
          </div>
        )}

        {/* Observaciones */}
        {ficha.observaciones && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Observaciones</h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              {ficha.observaciones}
            </p>
          </div>
        )}

        {/* Tratamientos */}
        {tratamientos.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <Pill className="w-5 h-5 mr-2 text-pastel-teal" />
              Tratamientos
            </h3>
            <div className="space-y-3">
              {tratamientos.map((tratamiento, index) => (
                <MobileCard key={index} className="bg-pastel-cream">
                  <MobileCardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-gray-900 text-sm">
                        {tratamiento.medicamento || tratamiento.nombre || 'Tratamiento'}
                      </h4>
                      {tratamiento.fecha && (
                        <span className="text-xs text-gray-500">
                          {formatDate(tratamiento.fecha)}
                        </span>
                      )}
                    </div>
                    {tratamiento.dosis && (
                      <p className="text-sm text-gray-700 mb-1">
                        <span className="font-medium">Dosis:</span> {tratamiento.dosis}
                      </p>
                    )}
                    {tratamiento.frecuencia && (
                      <p className="text-sm text-gray-700 mb-1">
                        <span className="font-medium">Frecuencia:</span> {tratamiento.frecuencia}
                      </p>
                    )}
                    {tratamiento.observaciones && (
                      <p className="text-sm text-gray-600 mt-2">
                        {tratamiento.observaciones}
                      </p>
                    )}
                  </MobileCardContent>
                </MobileCard>
              ))}
            </div>
          </div>
        )}

        {/* Seguimientos */}
        {seguimientos.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <Activity className="w-5 h-5 mr-2 text-pastel-teal" />
              Seguimientos
            </h3>
            <div className="space-y-3">
              {seguimientos.map((seguimiento, index) => (
                <MobileCard key={index} className="bg-pastel-cream">
                  <MobileCardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-gray-900 text-sm">
                        Seguimiento #{index + 1}
                      </h4>
                      {seguimiento.fecha && (
                        <span className="text-xs text-gray-500">
                          {formatDateTime(seguimiento.fecha)}
                        </span>
                      )}
                    </div>
                    {seguimiento.observaciones && (
                      <p className="text-sm text-gray-700">
                        {seguimiento.observaciones}
                      </p>
                    )}
                    {seguimiento.estado && (
                      <span className={`inline-block mt-2 px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(seguimiento.estado)}`}>
                        {seguimiento.estado}
                      </span>
                    )}
                  </MobileCardContent>
                </MobileCard>
              ))}
            </div>
          </div>
        )}

        {/* Botón Descargar PDF */}
        <Button
          onClick={handleDownloadPDF}
          disabled={downloading}
          className="w-full bg-pastel-teal hover:opacity-90 text-white"
        >
          <Download className="w-5 h-5 mr-2" />
          {downloading ? 'Descargando...' : 'Descargar PDF'}
        </Button>
      </div>
    </div>
  )
}
